/**
 * 法定基準一覧（Legal Welfare and Taxes > 法定基準一覧）の mock シード。
 *
 * 給与計算が金額・率・等級を法令に求める先を 10 件並べ、法定の 2 系統を網羅している:
 * - 法令が全国一律に定める料額表・税額表（社会保険 / 労働保険 / 所得税 / 通勤費非課税枠 / 割増率）
 * - 自治体通知を外部取込で持つもの（労働者別 住民税額）
 *
 * 系統は違っても「その表を誰が定めているか」は同じで、いずれも社外の法令が定める。
 * 住民税額は取込元が自治体通知でも地方税法に基づく賦課なのでここに載る。
 * 割増率表は「法定最低率と社内規程」の両方を持つが、表が存在する根拠は法定最低率なので
 * ここに寄せている。
 *
 * 顧客の賃金規程・経費規程が定める表（技術手当 / 号俸表 / 役職・家族手当 / 月次経費精算）は
 * 法令が定める表ではないため、参照先カタログ（`payrollReferenceCatalog.ts`）が
 * `coreSource: 'in-house'` として持つ。制定根拠の軸をエントリのフィールドで持たないのは、
 * その分岐が「どちらのカタログに載るか」そのものになったため。
 *
 * 説明（`description`）は 1 行目に「その表に何が入っているか」（照会キー・戻り値・粒度）を書き、
 * 2 行目以降に適用時期・端数処理・抜粋である旨などの但し書きを改行区切りで続ける。詳細画面の
 * ヘッダに出るのは 1 行目だけなので、1 行目だけで表の中身が分かるように書くこと。
 * 管轄プロダクト名（Country Plugin 等）は利用者向けの説明には書かない。出典は関連ソース
 * （`sources`）に持つ。
 *
 * 有効期間は基準日 2026-08-11 で全件が期間内になるよう置いた仮の値。
 * 一覧は `sortOrder` 昇順で並べる。
 */

import type { DemoDatabase } from '../schema';
import type { StatutoryStandardEntry } from '../types';
import { childcareContributionRateTable, commutingTaxExemptLimitTable, employmentInsuranceRateTable, healthInsurancePremiumTable, pensionInsurancePremiumTable, premiumRateTable, residentTaxPerWorkerTable, standardRemunerationGradeTable, withholdingTaxMonthlyTable, workersAccidentInsuranceRateTable } from './statutoryStandardTables';
export const STATUTORY_STANDARD_SEEDS: StatutoryStandardEntry[] = [{
  id: 'ss-commuting-tax-exempt-limit',
  name: '通勤費 非課税限度額表',
  description: '交通手段と片道通勤距離の区分ごとに、1 か月あたりの通勤手当の非課税限度額を並べた表。交通機関・有料道路／交通用具／定期乗車券／併用の区分で行が分かれ、交通用具は片道 2km 未満から 95km 以上までの距離帯ごとに限度額を持つ。距離で決まらない区分は限度額の代わりに「1 か月の合理的な運賃等の額（上限 150,000 円）」を返す。\n所得税法施行令第 20 条の 2。令和8年4月1日以後に支払われるべき通勤手当に適用します。\n片道距離の範囲は下限が「以上」（境界の額を含む）、上限が「未満」（境界を含まない）です。片道 2km 未満の交通用具利用は全額課税です。\n距離の区分を持たない区分（交通機関・定期乗車券・併用）は距離セルが「—」になります。\n「上記 + 駐車場等料金相当額」の「上記」は、同じ表の交通用具・距離区分に応じた非課税限度額を指します。\n改正は 2 段構えです。65km 以上の細分化と駐車場等料金の加算は令和8年度税制改正によるもので、2026 年 1〜3 月支払分には適用しません（支払日で分岐します）。\n改正前（令和7年4月〜令和8年3月）は 55km 以上が一律 38,700 円で駐車場等の加算もありませんでした。\n駐車場等は勤務地周辺、または通勤に利用する駅・停留所等の周辺の施設が対象で、自宅付近は対象外です。',
  payrollItemId: 'pi-i005',
  period: {
    start: '2024-01-01',
    end: null
  },
  sources: [{
    label: '国税庁 通勤手当の非課税限度額の引上げ（令和8年4月）',
    url: 'https://www.nta.go.jp/users/gensen/2026tsukin/pdf/01.pdf'
  }, {
    label: '国税庁 通勤手当の非課税限度額の引上げ（令和7年11月）',
    url: 'https://www.nta.go.jp/users/gensen/2025tsukin/pdf/01.pdf'
  }, {
    label: '国税庁 通勤手当の非課税限度額の引上げについて',
    url: 'https://www.nta.go.jp/users/gensen/2025tsukin/index.htm'
  }],
  table: commutingTaxExemptLimitTable,
  sortOrder: 1,
  updatedAt: '2026-08-01T09:00:00Z'
}, {
  id: 'ss-premium-rate',
  name: '割増率表',
  description: '割増賃金の種別ごとに法定の最低割増率を並べた表。時間外・月 60 時間超の時間外・深夜・法定休日と、それらが重なる場合の率を % で持ち、行ごとに根拠条文を添える。\n率は % の数値で持ちます（基本給の何 % を加算するか）。倍率は持ちません。深夜労働単独は月給者では基本給に既に含まれる時間への「加算」であり、時間外労働のように総額へ ×1.25 する話ではないため、行によって倍率の意味が変わってしまうからです。\nいずれも法定最低率です。就業規則・労働協約でこれを上回る率を定めた場合はその率が適用されます。\n月 60 時間超の 50% は中小企業にも令和5年4月1日から適用済みで、2026 年時点で企業規模による差はありません。\n月 60 時間の算定に法定休日の労働時間は含めませんが、法定外休日（所定休日）の労働時間は含めます。\n法定休日労働に時間外割増は加算しません（休日は 35% 単独、深夜が重なる場合のみ 60%）。\n時間外 25% / 休日 35% の具体率は労働基準法第37条第1項ではなく割増賃金令（平成6年政令第5号）が定めています。',
  payrollItemId: 'pi-i006',
  period: {
    start: '2023-04-01',
    end: null
  },
  sources: [{
    label: 'e-Gov 労働基準法 第37条',
    url: 'https://laws.e-gov.go.jp/law/322AC0000000049'
  }, {
    label: 'e-Gov 割増賃金令（平成6年政令第5号）',
    url: 'https://laws.e-gov.go.jp/law/406CO0000000005'
  }, {
    label: 'e-Gov 労働基準法施行規則 第20条',
    url: 'https://laws.e-gov.go.jp/law/322M40000100023'
  }, {
    label: '厚生労働省 月60時間超の割増賃金率引上げ',
    url: 'https://www.mhlw.go.jp/content/000930914.pdf'
  }],
  table: premiumRateTable,
  sortOrder: 2,
  updatedAt: '2026-08-01T09:00:00Z'
}, {
  id: 'ss-standard-remuneration-grade',
  name: '標準報酬月額 等級表（健保）',
  description: '報酬月額から健康保険の標準報酬月額と等級を引くための表。第 1〜50 等級それぞれについて、標準報酬月額と、その等級に入る報酬月額の範囲（下限以上・上限未満）を持ち、対応する厚生年金の等級を併記する。\n全 50 等級。等級・標準報酬月額・報酬月額の範囲は全国共通で、支部によって変わるのは保険料額のみです。\n報酬月額の範囲は下限が「以上」（境界の額を含む）、上限が「未満」（境界の額を含まない）です。境界の額はひとつ上の等級ではなく下の等級には入りません。\n第 1 等級は下限なし（63,000 円未満）、第 50 等級は上限なし（1,355,000 円以上）です。\n厚生年金では健保 4 等級（厚年 1 等級）の下限 83,000 円と健保 35 等級（厚年 32 等級）の上限 665,000 円を適用しません。\n被保険者負担分の端数は、給与控除時は 50 銭以下切捨・50 銭超切上、現金払時は 50 銭未満切捨・50 銭以上切上です（労使特約がある場合は特約が優先します）。',
  payrollItemId: 'pi-i007',
  period: {
    start: '2026-04-01',
    end: null
  },
  sources: [{
    label: '協会けんぽ 令和8年度 保険料額表（東京支部）',
    url: 'https://www.kyoukaikenpo.or.jp/assets/R8_13tokyo.pdf'
  }, {
    label: '協会けんぽ 令和8年度 都道府県毎の保険料率',
    url: 'https://www.kyoukaikenpo.or.jp/about/business/insurance_rate/rate_prefectures/r08/index.html'
  }],
  table: standardRemunerationGradeTable,
  sortOrder: 3,
  updatedAt: '2026-08-01T09:00:00Z'
}, {
  id: 'ss-health-insurance-premium',
  name: '健康保険料額表（都道府県別）',
  description: '都道府県と標準報酬月額の等級から健康保険料額を引くための表。47 都道府県 × 50 等級の各行に、健康保険料の全額と折半額、および介護保険第 2 号被保険者に適用する介護保険料込みの全額と折半額を持つ。\n令和8年3月分（4月納付分）から適用。全 47 都道府県 × 全 50 等級の保険料額を計算済みで持ちます。\n報酬月額の範囲は下限が「以上」（境界の額を含む）、上限が「未満」（境界の額を含まない）です。報酬月額がこの範囲に入る人にその等級の標準報酬月額を適用します。第 1 等級は下限なし、第 50 等級は上限なしです。\n保険料額 = 標準報酬月額 × 都道府県別の健康保険料率。折半額はその 2 分の 1 で、事業主と被保険者が半分ずつ負担します。\n「介護保険料含む」は 40〜64 歳の介護保険第 2 号被保険者に適用する額で、健康保険料率に介護保険料率 1.62%（全国一律）を加えて計算しています。\n折半額に生じる端数は、給与から控除する場合は 50 銭以下切捨・50 銭超切上、被保険者が現金で支払う場合は 50 銭未満切捨・50 銭以上切上です（労使特約がある場合は特約が優先します）。\n子ども・子育て支援金 0.23% は令和8年4月分（5月納付分）から始まる別枠のため、この表の額には含みません。\n全国平均 9.9%、最低は新潟県 9.21%、最高は佐賀県 10.55% です。標準賞与額の上限は年間 573 万円（4月1日〜翌年3月31日の累計）です。',
  payrollItemId: 'pi-i008',
  period: {
    start: '2026-03-01',
    end: null
  },
  sources: [{
    label: '協会けんぽ 令和8年度 都道府県毎の保険料率',
    url: 'https://www.kyoukaikenpo.or.jp/about/business/insurance_rate/rate_prefectures/r08/index.html'
  }, {
    label: '協会けんぽ 令和8年度 保険料額表（東京支部）',
    url: 'https://www.kyoukaikenpo.or.jp/assets/R8_13tokyo.pdf'
  }, {
    label: '協会けんぽ 特定保険料率・基本保険料率',
    url: 'https://www.kyoukaikenpo.or.jp/about/business/insurance_rate/001/index.html'
  }],
  table: healthInsurancePremiumTable,
  sortOrder: 4,
  updatedAt: '2026-08-01T09:00:00Z'
}, {
  id: 'ss-pension-insurance-premium',
  name: '厚生年金保険料額表',
  description: '標準報酬月額の等級から厚生年金保険料額を引くための表。第 1〜32 等級の各行に、報酬月額の範囲と、保険料率 18.300% による全額および労使折半後の 9.150% の額を持つ。\n全 32 等級（第 1 等級 88,000 円 〜 第 32 等級 650,000 円）。令和8年度も上限 650,000 円で引上げはありません。\n保険料率 18.300% は平成29年9月分から固定で、労使折半のため被保険者負担は 9.150% です。\n第 1 等級は下限なし（93,000 円未満）、第 32 等級は上限なし（635,000 円以上）です。\n厚生年金基金の加入員は免除保険料率 2.4%〜5.0% を控除した 13.300%〜15.900% になります（基金ごとに異なります）。\n標準賞与額の上限は 1 か月あたり 150 万円で、健康保険の年間 573 万円とは別ルールです。',
  payrollItemId: 'pi-i009',
  period: {
    start: '2020-09-01',
    end: null
  },
  sources: [{
    label: '日本年金機構 厚生年金保険料額表',
    url: 'https://www.nenkin.go.jp/service/kounen/hokenryo/ryogaku/ryogakuhyo/20200825.html'
  }, {
    label: '日本年金機構 令和8年度 保険料額表',
    url: 'https://www.nenkin.go.jp/service/kounen/hokenryo/ryogaku/ryogakuhyo/20200825.files/R08ryougaku.pdf'
  }],
  table: pensionInsurancePremiumTable,
  sortOrder: 5,
  updatedAt: '2026-08-01T09:00:00Z'
}, {
  id: 'ss-childcare-contribution-rate',
  name: '子ども・子育て拠出金率表',
  description: '標準報酬月額の等級から子ども・子育ての拠出金額・支援金額を引くための表。全額事業主負担の拠出金と労使折半の支援金を「種別」列で分け、等級ごとに全額・被保険者負担・事業主負担の 3 つの額を持つ。\n拠出金と支援金は別制度です。混同すると給与計算が壊れます。「種別」列で区別しています。\n子ども・子育て拠出金 0.36% は厚生年金保険の標準報酬月額（32 等級）が対象で、全額事業主負担です。被保険者からの控除は行わないため、被保険者負担は 0 円になります。\n子ども・子育て支援金 0.23% は健康保険の標準報酬月額（50 等級）が対象で、労使折半です。令和8年4月分（5月納付分）から適用されます。\n報酬月額の範囲は下限が「以上」（境界の額を含む）、上限が「未満」（境界の額を含まない）です。\n賞与の上限は拠出金・厚生年金が 1 か月 150 万円、支援金・健康保険が年間 573 万円（4月1日〜翌年3月31日の累計）です。この表は月額のみで賞与額は含みません。\n支援金の総額は令和8年度から段階的に増える予定のため、料率は年度ごとに改定される想定で扱います。',
  payrollItemId: 'pi-i010',
  period: {
    start: '2026-04-01',
    end: '2027-03-31'
  },
  sources: [{
    label: '日本年金機構 令和8年度 保険料額表',
    url: 'https://www.nenkin.go.jp/service/kounen/hokenryo/ryogaku/ryogakuhyo/20200825.files/R08ryougaku.pdf'
  }, {
    label: '厚生労働省 子ども・子育て支援金制度について',
    url: 'https://www.mhlw.go.jp/content/12401000/001670257.pdf'
  }, {
    label: '協会けんぽ 子ども・子育て支援金率',
    url: 'https://www.kyoukaikenpo.or.jp/about/business/insurance_rate/003/index.html'
  }, {
    label: '日本年金機構 子ども・子育て拠出金率',
    url: 'https://www.nenkin.go.jp/service/kounen/hokenryo/kyoshutsukin/index.html'
  }],
  table: childcareContributionRateTable,
  sortOrder: 6,
  updatedAt: '2026-08-01T09:00:00Z'
}, {
  id: 'ss-employment-insurance-rate',
  name: '雇用保険料率表',
  description: '事業の種類から雇用保険料率を引くための表。一般の事業／農林水産・清酒製造の事業／建設の事業ごとに、労働者負担・事業主負担・合計の率を 1,000 分率で持つ。\n率は公式の公表単位である 1,000 分率の数値で持ちます（% にするには 10 で割ります。5 なら 0.5%）。\n令和8年4月1日〜令和9年3月31日に適用。令和7年度から失業等給付等の料率が労使とも 0.5/1,000 引下げられました。\n労働者負担は失業等給付・育児休業給付の料率のみです。事業主負担に含まれる雇用保険二事業の料率（一般・農林水産等 3.5、建設 4.5）は事業主のみが負担するため、労働者の控除額に含めてはいけません。\n園芸サービス・牛馬の育成・酪農・養鶏・養豚・内水面養殖および特定の船員を雇用する事業は「農林水産の事業」ではなく一般の事業の料率を適用します。\n料率は年度単位で改定されるため、賃金支払期ではなく労働させた期間が属する年度で判定します。',
  payrollItemId: 'pi-i011',
  period: {
    start: '2026-04-01',
    end: '2027-03-31'
  },
  sources: [{
    label: '厚生労働省 令和8年度の雇用保険料率',
    url: 'https://www.mhlw.go.jp/content/001692566.pdf'
  }],
  table: employmentInsuranceRateTable,
  sortOrder: 7,
  updatedAt: '2026-08-01T09:00:00Z'
}, {
  id: 'ss-workers-accident-insurance-rate',
  name: '労災保険料率表',
  description: '事業の種類から労災保険率を引くための表。全 54 業種を分類と業種番号つきで並べ、労災保険率を 1,000 分率で持つ。請負による建設事業には、賃金総額の算定に使う労務費率を併記する。\n率は公式の公表単位である 1,000 分率の数値で持ちます（% にするには 10 で割ります。52 なら 5.2%）。\n全 54 業種。令和6年4月1日改定の率で、令和8年度も令和7年度と同率で据置です（原則 3 年ごとの改定）。\n労災保険料は全額事業主負担で、労働者負担はありません。\n労務費率は、請負による建設事業（番号 31〜38）で賃金総額を正確に把握することが困難な場合に、請負金額へ乗じて保険料を算定する率です。建設事業以外は「—」になります。\n最高は番号 21（金属鉱業・非金属鉱業又は石炭鉱業）の 88、最低は 2.5（番号 24・60・97・99）です。\n番号は徴収法施行規則別表第 1 の業種番号で連番ではありません（欠番があります）。',
  payrollItemId: 'pi-i012',
  period: {
    start: '2024-04-01',
    end: null
  },
  sources: [{
    label: '厚生労働省 労災保険率表',
    url: 'https://www.mhlw.go.jp/content/rousaihokenritu_r05.pdf'
  }, {
    label: '厚生労働省 労務費率表',
    url: 'https://www.mhlw.go.jp/content/roumuhiritu_r05.pdf'
  }, {
    label: '厚生労働省 労災保険率の改定について',
    url: 'https://www.mhlw.go.jp/stf/seisakunitsuite/bunya/koyou_roudou/roudoukijun/rousai/rousaihoken06/rousai_hokenritsu_kaitei.html'
  }],
  table: workersAccidentInsuranceRateTable,
  sortOrder: 8,
  updatedAt: '2026-08-01T09:00:00Z'
}, {
  id: 'ss-withholding-tax-monthly',
  name: '給与所得の源泉徴収税額表（月額表）',
  description: '社会保険料等控除後の給与等の額から源泉徴収税額を引くための表。金額帯ごとの行に、甲欄の扶養親族等 0〜7 人それぞれの税額と、乙欄の税額を並べる。\n令和8年分。甲欄は扶養親族等 0〜7 人、乙欄まで全 232 行を国税庁の月額表から取り込んでいます。\n課税対象額の範囲は下限が「以上」（境界の額を含む）、上限が「未満」（境界を含まない）です。\n甲欄で税額が 0 円になる上限は令和8年分から 105,000 円です（令和7年分の 88,000 円から引上げ）。88,000 円のまま実装すると全レンジがずれます。\n表として持つのは 740,000 円未満までです。740,000 円超は階段ではなく式のため行にしていません。740,000 円の基準税額は甲 0 人 71,680 / 1 人 65,210 / 2 人 58,750 / 3 人 52,290 / 4 人 45,810 / 5 人 39,350 / 6 人 32,890 / 7 人 26,410 / 乙欄 259,200 円で、これに超過額へ 20.42%（〜790,000 円未満）、23.483%（〜960,000 円未満）、33.693%（〜1,710,000 円未満）、40.84%（〜3,500,000 円未満）、45.945%（3,500,000 円超）を乗じて加算します。\n扶養親族等が 7 人を超える場合は 7 人の税額から超過 1 人につき 1,610 円を控除します。従たる給与についての扶養控除等申告書が提出されている場合も、申告された扶養親族等 1 人につき 1,610 円を乙欄の税額から控除します。\n令和8年度税制改正（基礎控除の更なる引上げ）は令和8年分の月額表には反映されません。効果は令和8年分の年末調整と令和9年分の税額表で出ます。',
  payrollItemId: 'pi-i013',
  period: {
    start: '2026-01-01',
    end: '2026-12-31'
  },
  sources: [{
    label: '国税庁 令和8年分 源泉徴収税額表',
    url: 'https://www.nta.go.jp/publication/pamph/gensen/zeigakuhyo2026/01.htm'
  }, {
    label: '国税庁 令和8年分 月額表（PDF）',
    url: 'https://www.nta.go.jp/publication/pamph/gensen/zeigakuhyo2026/data/01-07.pdf'
  }, {
    label: '国税庁 令和8年度税制改正による源泉徴収事務',
    url: 'https://www.nta.go.jp/publication/pamph/gensen/2026kaisei.pdf'
  }, {
    label: '国税庁 令和8年分 月額表（Excel 1〜7ページ）',
    url: 'https://www.nta.go.jp/publication/pamph/gensen/zeigakuhyo2026/data/01-07.xls'
  }],
  table: withholdingTaxMonthlyTable,
  sortOrder: 9,
  updatedAt: '2026-08-01T09:00:00Z'
}, {
  id: 'ss-resident-tax-per-worker',
  name: '労働者別 住民税額（特別徴収税額通知）',
  description: '労働者ごとに特別徴収する住民税額を並べた表。年度と課税市区町村とあわせて、年税額と、100 円未満の端数を 6 月に寄せたあとの 6 月分・7 月〜翌 5 月分の月額を持つ。\n個人別の税額は市町村の特別徴収税額決定通知書が唯一の正です。DRESS CODE は外部取込でしか値を持たないため、この表はサンプル値（架空の従業員）です。\n給与システム側で所得割を再計算してはいけません（自治体側で人的控除・調整控除・寄附金税額控除等を反映済みです）。取り込むのは通知値で、システムがするのは 6 月への端数寄せと 12 分割だけにとどめます。\n100 円未満の端数は最初の納期限（6 月分）に合算します（地方税法 第20条の4の2 第6項・第8項）。7 月〜翌 5 月分 = 年税額 ÷ 12 の 100 円未満切捨、6 月分 = 年税額 − 7 月〜翌 5 月分 × 11 です。\n標準税率は均等割が市町村民税 3,000 円・道府県民税 1,000 円・森林環境税 1,000 円（合計 5,000 円）、所得割が市町村民税 6%・道府県民税 4%（合計 10%）です。指定都市は所得割の市・県内訳が 8% / 2% になります（合計 10% は不変）。\n税額決定通知は当該年度の 5 月 31 日までに自治体から通知されます。令和8年度分は賦課期日 2026 年 1 月 1 日、課税対象は前年（2025 年）の所得で、2026 年 6 月〜2027 年 5 月に徴収します。\n年税額が均等割額以下の場合は 6 月に全額を一括徴収します。均等割は令和6年度から復興特別税が終了し森林環境税 1,000 円が併課されています。',
  payrollItemId: 'pi-i014',
  period: {
    start: '2026-06-01',
    end: '2027-05-31'
  },
  sources: [{
    label: 'e-Gov 地方税法',
    url: 'https://laws.e-gov.go.jp/law/325AC0000000226'
  }, {
    label: 'e-Gov 森林環境税及び森林環境譲与税に関する法律',
    url: 'https://laws.e-gov.go.jp/law/431AC0000000003'
  }, {
    label: '総務省 個人住民税',
    url: 'https://www.soumu.go.jp/main_sosiki/jichi_zeisei/czaisei/czaisei_seido/150790_06.html'
  }],
  table: residentTaxPerWorkerTable,
  sortOrder: 10,
  updatedAt: '2026-08-01T09:00:00Z'
}];
export async function seedStatutoryStandardData(db: DemoDatabase): Promise<void> {
  if ((await db.statutoryStandardEntries.count()) === 0) {
    await db.statutoryStandardEntries.bulkAdd(STATUTORY_STANDARD_SEEDS);
  }
}